import { Component, type ReactNode } from 'react';

interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
  /** If true, the fallback covers the whole viewport instead of the panel slot. */
  fullScreen?: boolean;
  /** Optional label so we know which boundary fired. */
  label?: string;
}

/**
 * Catches render errors so a bad component doesn't crash the whole app.
 * Shows a diagnostic box with the stack so we can fix it instead of staring
 * at a black screen.
 */
export class PanelErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.label ? ' ' + this.props.label : ''}]`,
      error,
      info.componentStack,
    );
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      const fullScreenStyle: React.CSSProperties = this.props.fullScreen
        ? { position: 'fixed', inset: 0, width: 'auto', height: 'auto', zIndex: 100 }
        : {};
      return (
        <aside
          data-testid="panel-error-boundary"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 'min(420px, 100vw)',
            background: 'var(--surface-raised)',
            borderLeft: '1px solid var(--border)',
            boxShadow: 'var(--shadow-elevated)',
            zIndex: 20,
            overflowY: 'auto',
            padding: 'var(--space-4)',
            fontFamily: 'var(--font-body)',
            color: 'var(--text)',
            ...fullScreenStyle,
          }}
        >
          <h2
            style={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: '#c0392b',
              marginBottom: 'var(--space-2)',
            }}
          >
            {this.props.label ? `${this.props.label} render error` : 'Render error'}
          </h2>
          <p style={{ fontSize: '0.85rem', marginBottom: 'var(--space-3)' }}>
            {this.state.error.message}
          </p>
          <pre
            style={{
              fontSize: '0.7rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: 'var(--surface)',
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              maxHeight: 240,
              overflow: 'auto',
            }}
          >
            {this.state.error.stack ?? '(no stack)'}
          </pre>
          <button
            type="button"
            onClick={this.reset}
            style={{
              marginTop: 'var(--space-3)',
              padding: '6px 12px',
              fontSize: '0.85rem',
              fontWeight: 600,
              background: 'var(--primary)',
              color: '#fffdf2',
              border: '1px solid var(--primary)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </aside>
      );
    }
    return this.props.children;
  }
}
